import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, User, Tag, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import type { BlogConfig, BlogPost, Pagination, BlogThemeStyles } from './types';
import {
  getCroppedImageStyles,
  hasCropApplied,
} from '../../components/ComponentEditModal/shared/cropUtils';
import type { CropData } from '../../components/ComponentEditModal/shared/ImageCropModal';

interface FontStyles {
  headingFont: React.CSSProperties;
  bodyFont: React.CSSProperties;
}

interface BlogListViewProps {
  blog: BlogConfig;
  posts: BlogPost[];
  pagination: Pagination | null;
  tags: Array<{ tag: string; count: number }>;
  theme: BlogThemeStyles;
  fontStyles?: FontStyles;
  wrapInLayout?: boolean;
  currentPage: number;
  selectedTag?: string;
  searchQuery: string;
  activeSearch: string;
  onPageChange: (page: number) => void;
  onSearchChange: (query: string) => void;
  onSearchSubmit: () => void;
  onClearSearch: () => void;
}

export const BlogListView: React.FC<BlogListViewProps> = ({
  blog,
  posts,
  pagination,
  tags,
  theme,
  fontStyles,
  wrapInLayout = false,
  currentPage,
  selectedTag,
  searchQuery,
  activeSearch,
  onPageChange,
  onSearchChange,
  onSearchSubmit,
  onClearSearch,
}) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen" style={{ background: theme.pageBackground }}>
      {/* Header */}
      <header
        style={{
          backgroundColor: theme.headerBackground,
          borderBottom: `1px solid ${theme.cardBorder}`,
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-3xl font-bold"
                style={{ color: theme.titleColor, ...fontStyles?.headingFont }}
              >
                {blog.title}
              </h1>
              {blog.description && (
                <p
                  className="mt-2"
                  style={{ color: theme.descriptionColor, ...fontStyles?.bodyFont }}
                >
                  {blog.description}
                </p>
              )}
            </div>
            {!wrapInLayout && (
              <a
                href="/"
                className="transition-colors"
                style={{ color: theme.linkColor }}
                onMouseOver={e => (e.currentTarget.style.color = theme.linkHoverColor || '')}
                onMouseOut={e => (e.currentTarget.style.color = theme.linkColor || '')}
              >
                ← Back to site
              </a>
            )}
          </div>

          {/* Search bar */}
          <div className="mt-6">
            <form
              onSubmit={e => {
                e.preventDefault();
                onSearchSubmit();
              }}
              className="flex gap-2"
            >
              <div className="relative flex-1 max-w-md">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                  style={{ color: theme.mutedTextColor }}
                />
                <input
                  type="text"
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={e => onSearchChange(e.target.value)}
                  className="w-full pl-10 pr-10 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
                  style={{
                    backgroundColor: theme.cardBackground,
                    borderColor: theme.cardBorder,
                    color: theme.textColor,
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={onClearSearch}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: theme.mutedTextColor }}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{
                  backgroundColor: theme.linkColor,
                  color: '#ffffff',
                }}
              >
                Search
              </button>
            </form>
          </div>

          {/* Tag filter indicator */}
          {selectedTag && (
            <div className="mt-4 flex items-center gap-2">
              <span style={{ color: theme.textColor }}>Showing posts tagged:</span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  background: theme.tagActiveBackground,
                  color: theme.tagActiveText,
                }}
              >
                <Tag className="w-3 h-3 mr-1" />
                {selectedTag}
              </span>
              <Link
                to={blog.basePath}
                className="transition-colors"
                style={{ color: theme.linkColor }}
                onMouseOver={e => (e.currentTarget.style.color = theme.linkHoverColor || '')}
                onMouseOut={e => (e.currentTarget.style.color = theme.linkColor || '')}
              >
                Clear filter
              </Link>
            </div>
          )}

          {/* Search results indicator */}
          {activeSearch && (
            <div className="mt-4 flex items-center gap-2">
              <span style={{ color: theme.textColor }}>Search results for:</span>
              <span
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  background: theme.tagActiveBackground,
                  color: theme.tagActiveText,
                }}
              >
                <Search className="w-3 h-3 mr-1" />
                {activeSearch}
              </span>
              <button
                onClick={onClearSearch}
                className="transition-colors"
                style={{ color: theme.linkColor }}
                onMouseOver={e => (e.currentTarget.style.color = theme.linkHoverColor || '')}
                onMouseOut={e => (e.currentTarget.style.color = theme.linkColor || '')}
              >
                Clear search
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Posts */}
          <div className="lg:col-span-3">
            {posts.length === 0 ? (
              <div
                className="text-center py-12 rounded-lg"
                style={{
                  backgroundColor: theme.cardBackground,
                  boxShadow: theme.cardShadow,
                }}
              >
                <p style={{ color: theme.textColor }}>No posts found.</p>
              </div>
            ) : (
              <div
                className={
                  blog.layout === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'space-y-6'
                }
              >
                {posts.map((post, index) => (
                  <article
                    key={post.id}
                    className={`rounded-lg overflow-hidden transition-shadow ${
                      blog.layout === 'magazine' && index === 0 ? 'md:col-span-2' : ''
                    }`}
                    style={{
                      backgroundColor: theme.cardBackground,
                      border: `1px solid ${theme.cardBorder}`,
                      boxShadow: theme.cardShadow,
                    }}
                    onMouseOver={e =>
                      (e.currentTarget.style.boxShadow = theme.cardHoverShadow || '')
                    }
                    onMouseOut={e => (e.currentTarget.style.boxShadow = theme.cardShadow || '')}
                  >
                    {blog.showFeaturedImage &&
                      post.featuredImageUrl &&
                      (() => {
                        const crop = post.featuredImageCrop as CropData | undefined;
                        const hasCrop = hasCropApplied(crop);
                        const imageStyle = getCroppedImageStyles(crop);
                        const heightClass = blog.layout === 'list' ? 'h-48 md:h-32' : 'h-48';

                        if (hasCrop) {
                          return (
                            <Link to={`${blog.basePath}/${post.slug}`}>
                              <div className={`relative w-full overflow-hidden ${heightClass}`}>
                                <img
                                  src={post.featuredImageUrl}
                                  alt={post.title}
                                  style={imageStyle}
                                />
                              </div>
                            </Link>
                          );
                        }

                        return (
                          <Link to={`${blog.basePath}/${post.slug}`}>
                            <img
                              src={post.featuredImageUrl}
                              alt={post.title}
                              className={`w-full object-cover ${heightClass}`}
                            />
                          </Link>
                        );
                      })()}

                    <div className="p-5">
                      <Link to={`${blog.basePath}/${post.slug}`}>
                        <h2
                          className="text-xl font-semibold transition-colors"
                          style={{ color: theme.postTitleColor, ...fontStyles?.headingFont }}
                          onMouseOver={e => (e.currentTarget.style.color = theme.linkColor || '')}
                          onMouseOut={e =>
                            (e.currentTarget.style.color = theme.postTitleColor || '')
                          }
                        >
                          {post.title}
                        </h2>
                      </Link>

                      {post.excerpt && (
                        <p
                          className="mt-2 line-clamp-2"
                          style={{ color: theme.textColor, ...fontStyles?.bodyFont }}
                        >
                          {post.excerpt}
                        </p>
                      )}

                      <div
                        className="mt-4 flex flex-wrap items-center gap-3 text-sm"
                        style={{ color: theme.mutedTextColor }}
                      >
                        {blog.showAuthor && post.authorName && (
                          <span className="flex items-center">
                            <User className="w-4 h-4 mr-1" style={{ color: theme.iconColor }} />
                            {post.authorName}
                          </span>
                        )}
                        {blog.showDate && (
                          <span className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" style={{ color: theme.iconColor }} />
                            {formatDate(post.publishedAt || post.createdAt)}
                          </span>
                        )}
                        {blog.showReadingTime && post.readingTimeMinutes && (
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" style={{ color: theme.iconColor }} />
                            {post.readingTimeMinutes} min
                          </span>
                        )}
                      </div>

                      {blog.showTags && post.tags && post.tags.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {post.tags.slice(0, 3).map(t => (
                            <Link
                              key={t}
                              to={`${blog.basePath}/tag/${t}`}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs transition-colors"
                              style={{
                                backgroundColor: theme.tagBackground,
                                color: theme.tagText,
                              }}
                            >
                              {t}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center space-x-4">
                <button
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: theme.paginationBackground,
                    color: theme.paginationText,
                    border: `1px solid ${theme.paginationBorder}`,
                    opacity:
                      currentPage === 1 ? parseFloat(theme.paginationDisabledOpacity || '0.5') : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </button>
                <span style={{ color: theme.textColor }}>
                  Page {currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => onPageChange(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  style={{
                    backgroundColor: theme.paginationBackground,
                    color: theme.paginationText,
                    border: `1px solid ${theme.paginationBorder}`,
                    opacity:
                      currentPage === pagination.totalPages
                        ? parseFloat(theme.paginationDisabledOpacity || '0.5')
                        : 1,
                    cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer',
                  }}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            {/* Tags */}
            {tags.length > 0 && (
              <div
                className="rounded-lg p-5"
                style={{
                  backgroundColor: theme.sidebarBackground,
                  boxShadow: theme.cardShadow,
                }}
              >
                <h3
                  className="text-lg font-semibold mb-4"
                  style={{ color: theme.sidebarTitleColor, ...fontStyles?.headingFont }}
                >
                  Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.map(({ tag: t, count }) => (
                    <Link
                      key={t}
                      to={`${blog.basePath}/tag/${t}`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm transition-colors"
                      style={{
                        background:
                          selectedTag === t ? theme.tagActiveBackground : theme.tagBackground,
                        color: selectedTag === t ? theme.tagActiveText : theme.tagText,
                      }}
                    >
                      {t}
                      <span className="ml-1 text-xs opacity-70">({count})</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};
